import type { ReactNode } from 'react'
import { FaSearch, FaSlidersH } from 'react-icons/fa'
import { FloatingPanel } from 'ui/FloatingPanel/FloatingPanel'
import styles from '../shared/mhlShared.module.scss'
import { REFERRAL_LABELS, referralLabel, UNKNOWN_REFERRAL } from '../shared/mhlFormatting'
import type { AudiologyResult, MhlLead, ReviewStatus } from '../shared/mhlTypes'
import { MhlLeadsFilterPanelContent } from './MhlLeadsFilterPanelContent'

export type MhlLeadSort = 'submitted_desc' | 'submitted_asc' | 'name_asc' | 'name_desc' | 'quantum' | 'esign'
export type MhlQuantumFilter = 'all' | 'above' | 'below' | 'tbc'
export type MhlAudiologyFilter = 'all' | AudiologyResult
export type MhlEsignFilter = 'all' | 'not_sent' | 'sent' | 'signed'
export type MhlReviewFilter = 'all' | ReviewStatus
/** 'all', `agent:<added_by>` for dashboard-onboarded leads, or `site:<lead_source>` for claim-site leads. */
export type MhlChannelFilter = string

export const MHL_LEAD_SORT_DEFAULT: MhlLeadSort = 'submitted_desc'

export interface MhlLeadFilterState {
    search: string
    sort: MhlLeadSort
    channel: MhlChannelFilter
    quantum: MhlQuantumFilter
    audiology: MhlAudiologyFilter
    esign: MhlEsignFilter
    review: MhlReviewFilter
    from: string
    to: string
}

export const MHL_LEAD_FILTER_DEFAULTS: MhlLeadFilterState = {
    search: '',
    sort: MHL_LEAD_SORT_DEFAULT,
    channel: 'all',
    quantum: 'all',
    audiology: 'all',
    esign: 'all',
    review: 'all',
    from: '',
    to: '',
}

export interface MhlChannelOption {
    value: MhlChannelFilter
    label: string
}

export interface MhlChannelOptions {
    agents: MhlChannelOption[]
    website: MhlChannelOption[]
}

// Untagged claim-site visits are taken to be the printed QR codes, which
// point at the bare site URL.
const SITE_CHANNEL_LABELS: Record<string, string> = {
    organic: 'NIHL Website (QR Code)',
    [UNKNOWN_REFERRAL]: 'Unknown (before tagging)',
}

const leadChannel = (lead: MhlLead): MhlChannelFilter =>
    lead.submitted_via === 'dashboard'
        ? `agent:${lead.added_by}`
        : `site:${lead.lead_source || UNKNOWN_REFERRAL}`

/**
 * Agent and website-source options for the channel filter, from the leads on screen.
 * With `includeWebsite`, the known sources (Sentinel Forces, QR code) are listed even with no leads yet.
 */
export const mhlChannelOptions = (leads: MhlLead[], includeWebsite: boolean): MhlChannelOptions => {
    const agents = new Map<string, string>()
    const sources = new Set<string>(includeWebsite ? Object.keys(REFERRAL_LABELS) : [])
    leads.forEach((l) => {
        if (l.submitted_via === 'dashboard') {
            if (!agents.has(l.added_by)) agents.set(l.added_by, l.added_by_name || l.added_by || 'Unknown agent')
        } else {
            sources.add(l.lead_source || UNKNOWN_REFERRAL)
        }
    })
    const byLabel = (a: MhlChannelOption, b: MhlChannelOption) => a.label.localeCompare(b.label)
    return {
        agents: Array.from(agents, ([key, label]) => ({ value: `agent:${key}`, label })).sort(byLabel),
        website: Array.from(sources, (s) => ({ value: `site:${s}`, label: SITE_CHANNEL_LABELS[s] || referralLabel(s) })).sort(byLabel),
    }
}

const dayKey = (value: string | null | undefined) => (value ? value.split('T')[0] : '')
const quantumRank = (q: MhlLead['quantum']) => (q === 'above' ? 0 : q === 'below' ? 1 : 2)
const esignRank = (lead: MhlLead) => (lead.esign.signed ? 0 : lead.esign.link_sent_at || lead.esign.status ? 1 : 2)

export const matchesMhlLeadSearch = (lead: MhlLead, q: string) => {
    if (!q) return true
    return [
        `${lead.firstname} ${lead.lastname}`,
        lead.nationalInsurance,
        lead.email,
        lead.telephone,
        lead.postcode,
        lead.case_ref,
        lead.added_by,
        lead.added_by_name || '',
    ]
        .join(' ')
        .toLowerCase()
        .includes(q)
}

/** Apply search, extra filters and sort for the lead-tracker table. */
export const filterAndSortMhlLeads = (leads: MhlLead[], filters: MhlLeadFilterState): MhlLead[] => {
    const q = filters.search.trim().toLowerCase()
    const list = leads.filter((l) => {
        if (!matchesMhlLeadSearch(l, q)) return false
        if (filters.channel !== 'all' && leadChannel(l) !== filters.channel) return false
        if (filters.quantum !== 'all') {
            const quantum = l.quantum || 'tbc'
            if (quantum !== filters.quantum) return false
        }
        if (filters.audiology !== 'all') {
            const audiology = l.audiology || 'tbc'
            if (audiology !== filters.audiology) return false
        }
        if (filters.esign === 'signed' && !l.esign.signed) return false
        if (filters.esign === 'sent' && (l.esign.signed || !(l.esign.link_sent_at || l.esign.status))) return false
        if (filters.esign === 'not_sent' && (l.esign.signed || l.esign.link_sent_at || l.esign.status)) return false
        if (filters.review !== 'all' && l.review_status !== filters.review) return false
        const day = dayKey(l.submitted_at)
        if (filters.from && day < filters.from) return false
        if (filters.to && day > filters.to) return false
        return true
    })
    list.sort((a, b) => {
        switch (filters.sort) {
            case 'submitted_asc':
                return dayKey(a.submitted_at).localeCompare(dayKey(b.submitted_at)) || a.id - b.id
            case 'name_asc':
                return `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`)
            case 'name_desc':
                return `${b.lastname} ${b.firstname}`.localeCompare(`${a.lastname} ${a.firstname}`)
            case 'quantum':
                return (
                    quantumRank(a.quantum) - quantumRank(b.quantum) ||
                    dayKey(b.submitted_at).localeCompare(dayKey(a.submitted_at))
                )
            case 'esign':
                return esignRank(a) - esignRank(b) || dayKey(b.submitted_at).localeCompare(dayKey(a.submitted_at))
            case 'submitted_desc':
            default:
                return dayKey(b.submitted_at).localeCompare(dayKey(a.submitted_at)) || b.id - a.id
        }
    })
    return list
}

const moreFilterCount = (filters: MhlLeadFilterState, showReview: boolean) =>
    [
        filters.channel !== 'all',
        filters.quantum !== 'all',
        filters.audiology !== 'all',
        filters.esign !== 'all',
        showReview && filters.review !== 'all',
        !!filters.from,
        !!filters.to,
    ].filter(Boolean).length

interface Props {
    filters: MhlLeadFilterState
    onChange: (next: MhlLeadFilterState) => void
    matched: number
    total: number
    /** Review filter only applies to dashboard-onboarded leads. */
    showReview?: boolean
    channelOptions: MhlChannelOptions
    /** Extra page actions, placed after More filters. */
    children?: ReactNode
}

/**
 * Lead-tracker filter row: search + sort inline, More filters opens a FloatingPanel.
 */
export const MhlLeadsFilter = ({
    filters,
    onChange,
    matched,
    total,
    showReview = false,
    channelOptions,
    children,
}: Props) => {
    const patch = (partial: Partial<MhlLeadFilterState>) => onChange({ ...filters, ...partial })
    const moreActive = moreFilterCount(filters, showReview)

    return (
        <div className={styles.toolbar}>
            <div className={styles.searchWrap}>
                <FaSearch className={styles.searchIcon} />
                <input
                    className={styles.searchInput}
                    placeholder="Search name, NI, email, mobile, postcode, case ref or agent…"
                    value={filters.search}
                    onChange={(e) => patch({ search: e.target.value })}
                />
            </div>
            <select
                className={`${styles.select} ${styles.toolbarControl}`}
                value={filters.sort}
                onChange={(e) => patch({ sort: e.target.value as MhlLeadSort })}
                aria-label="Sort leads"
            >
                <option value="submitted_desc">Newest first</option>
                <option value="submitted_asc">Oldest first</option>
                <option value="name_asc">Name A → Z</option>
                <option value="name_desc">Name Z → A</option>
                <option value="quantum">Quantum</option>
                <option value="esign">E-sign status</option>
            </select>
            <FloatingPanel
                triggerIcon={<FaSlidersH size={12} aria-hidden />}
                triggerLabel={
                    <>
                        More filters
                        {moreActive > 0 && <span className={styles.btnCount}>{moreActive}</span>}
                    </>
                }
                ariaLabel="More lead filters"
                className={styles.toolbarControl}
                title="More filters"
                width={280}
            >
                <MhlLeadsFilterPanelContent
                    filters={filters}
                    onChange={onChange}
                    showReview={showReview}
                    channelOptions={channelOptions}
                />
            </FloatingPanel>
            {children}
            <span className={styles.resultCount}>
                {matched} of {total}
            </span>
        </div>
    )
}
